import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  type CollectionReference,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { wordleMonthlyScoreDocumentConverter } from "@kekkle/shared/convertors/web/wordle-monthly-score-document.converter";
import type { WordleMonthlyScoreDocument } from "@kekkle/shared/types/documents/wordle-monthly-score-document/wordle-monthly-score-document";
import type {
  FetchMonthlyScoresOptions,
  FetchMonthlyScoresResult,
} from "@/types/firestore/wordle";

/**
 * Returns a reference to the wordle6 monthly scores collection with converter
 */
function wordle6MonthlyScoresCollection(): CollectionReference<WordleMonthlyScoreDocument> {
  return collection(firestore, "wordle6_monthly_scores").withConverter(
    wordleMonthlyScoreDocumentConverter,
  );
}

/**
 * Fetches the wordle6 monthly scores
 * @param year - Year
 * @param month - Month (1-12)
 * @param userFilter - Array of user IDs to filter by (optional)
 * @param options - Pagination options
 * @returns Object containing documents, last document snapshot, and hasMore flag
 */
export async function fetchMonthlyScores(
  year: number,
  month: number,
  userFilter: string[] = [],
  options: FetchMonthlyScoresOptions = {},
): Promise<FetchMonthlyScoresResult> {
  const { pageSize = 10, lastDoc } = options;
  const docs: WordleMonthlyScoreDocument[] = [];
  const userFilterBatch: string[][] = [];
  let finalLastDoc: QueryDocumentSnapshot<DocumentData> | undefined;

  // Split the userFilter into multiple arrays in the userFilterBatch array
  for (let i = 0; i < userFilter.length; i += 25) {
    userFilterBatch.push(userFilter.slice(i, i + 25));
  }

  // Loop through the userFilterBatch array
  for (let i = 0; i < userFilterBatch.length || i === 0; i++) {
    let scoresQuery = query(
      wordle6MonthlyScoresCollection(),
      where("month", "==", month),
      where("year", "==", year),
      orderBy("new_score", "desc"),
    );

    if (userFilterBatch[i]) {
      scoresQuery = query(
        scoresQuery,
        where("user_id", "in", userFilterBatch[i]),
      );
    }

    if (lastDoc && i === 0) {
      scoresQuery = query(scoresQuery, startAfter(lastDoc));
    }

    scoresQuery = query(scoresQuery, limit(pageSize));

    const querySnapshot = await getDocs(scoresQuery);
    const documents = querySnapshot.docs.map((doc) => doc.data());
    documents.forEach((doc) => docs.push(doc));

    if (querySnapshot.docs.length > 0) {
      finalLastDoc = querySnapshot.docs[
        querySnapshot.docs.length - 1
      ] as QueryDocumentSnapshot<DocumentData>;
    }

    // Only fetch from first batch for pagination
    if (docs.length >= pageSize) break;
  }

  return {
    docs,
    lastDoc: finalLastDoc,
    hasMore: docs.length === pageSize,
  };
}
